import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, Divider,
  Grid, IconButton, InputAdornment, MenuItem, Paper, Select, TextField,
  Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotesIcon from "@mui/icons-material/Notes";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useCreateShopMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate, useParams } from "react-router-dom";
import PageLoader from "../../../Components/Loader/PageLoader";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { parseNominatimResult, splitStreetNumberFromName } from "../../../utils/parseAddress";
import { useShopUniquenessCheck } from "../../../utils/useShopUniquenessCheck";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const LARAVEL_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

// ─── Phone helpers ────────────────────────────────────────────────────────────
const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const stripPhone = (formatted: string): string => formatted.replace(/\D/g, "");
const validatePhone = (value: string): string | undefined => {
  if (!value) return undefined;
  const digits = stripPhone(value);
  if (digits.length > 0 && digits.length !== 10) return "Phone number must be 10 digits — e.g. (416) 555-1234";
  return undefined;
};
const validateEmail = (value: string): string | undefined => {
  if (!value) return undefined;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? undefined : "Invalid email address";
};

interface NominatimResult {
  display_name: string;
  address: { house_number?: string; road?: string; city?: string; town?: string; village?: string; state?: string; postcode?: string };
}

const TIME_SLOTS: string[] = [
  "Anytime", "Early Morning (7am – 9am)", "Morning (9am – 12pm)", "Afternoon (12pm – 3pm)", "Late Afternoon (3pm – 6pm)", "Evening (6pm – 9pm)",
  "7:00 AM", "7:30 AM", "8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
  "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM",
];

const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider", bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "grey.50" }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: "8px", bgcolor: "primary.main", color: "#fff", flexShrink: 0 }}>{icon}</Box>
    <Typography fontWeight={600} fontSize={14} color="text.primary">{title}</Typography>
  </Box>
);

const Field = ({ label, required, children, cols = 6 }: { label: string; required?: boolean; children: React.ReactNode; cols?: number }) => (
  <Grid item lg={cols} md={cols} sm={12} xs={12}>
    <Typography variant="body2" fontWeight={500} mb={0.5} color="text.secondary">
      {label}{required && <Box component="span" color="error.main" ml={0.3}>*</Box>}
    </Typography>
    {children}
  </Grid>
);

interface ShopEditProps {
  openModel?: boolean;
  handleCloseDialog?: (close?: boolean) => void;
  data?: any;
}

const ShopEdit: React.FC<ShopEditProps> = ({ openModel, handleCloseDialog, data: propData }) => {
  const params = useParams<{ id: string }>();
  const id = propData?.id ? String(propData.id) : params.id;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [createShop, { isLoading }] = useCreateShopMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [shopData, setShopData] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [existingImage, setExistingImage] = useState("");
  const [isImageUploading, setIsImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Address autocomplete
  const [addressOptions, setAddressOptions] = useState<NominatimResult[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressInput, setAddressInput] = useState("");
  const [addressOpen, setAddressOpen] = useState(false);
  const addressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load shop data
  useEffect(() => {
    if (propData) {
      setShopData(propData);
      if (propData.customerImg) setExistingImage(propData.customerImg);
      setPageLoading(false);
      return;
    }
    if (!id) return;
    setPageLoading(true);
    const t = localStorage.getItem("token") || "";
    fetch(`${API_URL}/shop/getAll`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` },
      body: JSON.stringify({ searchText: "", id }),
    })
      .then(r => r.json())
      .then(json => {
        const shops = json?.data || [];
        const shop = shops.find((s: any) => String(s.id) === String(id));
        if (shop) {
          setShopData(shop);
          if (shop.customerImg) setExistingImage(shop.customerImg);
        }
      })
      .catch(() => {})
      .finally(() => setPageLoading(false));
  }, [id, propData]);

  const searchAddress = async (query: string) => {
    if (query.trim().length < 4) { setAddressOptions([]); setAddressOpen(false); return; }
    setAddressLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=ca&limit=6`, { headers: { "Accept-Language": "en", "User-Agent": "POS-App/1.0" } });
      const data: NominatimResult[] = await res.json();
      setAddressOptions(data); setAddressOpen(data.length > 0);
    } catch { setAddressOptions([]); }
    finally { setAddressLoading(false); }
  };

  const handleAddressInputChange = (value: string) => {
    setAddressInput(value);
    if (addressTimer.current) clearTimeout(addressTimer.current);
    addressTimer.current = setTimeout(() => searchAddress(value), 450);
  };

  const handleAddressSelect = (result: NominatimResult) => {
    const parsed = parseNominatimResult(result);
    formik.setFieldValue("customerStreetNumber", parsed.streetNumber);
    formik.setFieldValue("customerStreetName", parsed.streetName);
    formik.setFieldValue("customerCity", parsed.city);
    formik.setFieldValue("customerProvince", parsed.province);
    formik.setFieldValue("customerPostalCode", parsed.postalCode);
    setAddressInput(result.display_name);
    setAddressOptions([]); setAddressOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      customerFirstName: "", customerLastName: "", customerMobileNum: "", customerEmail: "",
      customerProvince: "", customerCity: "", customerPostalCode: "",
      customerStreetNumber: "", customerStreetName: "", customerDescription: "",
      customerCompanyName: "", customerCompanyPhoneNumebr: "", customerCompanyEmail: "",
      customerPreferTimeToCall: "", customerImg: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      // Required fields per spec
      if (!values.customerCompanyName.trim())          errors.customerCompanyName       = "Restaurant name is required";
      if (!values.customerCompanyPhoneNumebr.trim())   errors.customerCompanyPhoneNumebr = "Company phone is required";
      else { const e4 = validatePhone(values.customerCompanyPhoneNumebr); if (e4) errors.customerCompanyPhoneNumebr = e4; }
      if (!values.customerFirstName.trim())            errors.customerFirstName        = "First name is required";
      if (!values.customerLastName.trim())             errors.customerLastName         = "Last name is required";
      if (!values.customerMobileNum.trim())            errors.customerMobileNum        = "Mobile number is required";
      else { const e3 = validatePhone(values.customerMobileNum); if (e3) errors.customerMobileNum = e3; }
      if (!values.customerStreetNumber.trim())         errors.customerStreetNumber     = "Street number is required";
      if (!values.customerStreetName.trim())           errors.customerStreetName       = "Street name is required";
      if (!values.customerCity.trim())                 errors.customerCity             = "City is required";
      if (!values.customerProvince.trim())             errors.customerProvince         = "Province is required";
      if (!values.customerPostalCode.trim())           errors.customerPostalCode       = "Postal code is required";
      // Optional fields, format-validated only when filled
      if (values.customerEmail.trim())        { const e1 = validateEmail(values.customerEmail);        if (e1) errors.customerEmail        = e1; }
      if (values.customerCompanyEmail.trim()) { const e2 = validateEmail(values.customerCompanyEmail); if (e2) errors.customerCompanyEmail = e2; }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        let customerImg = existingImage || "";
        if (selectedImage) {
          setIsImageUploading(true);
          customerImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const payload = {
          ...values, id,
          customerMobileNum: stripPhone(values.customerMobileNum),
          customerCompanyPhoneNumebr: stripPhone(values.customerCompanyPhoneNumebr),
          customerImg,
        };
        const res = await createShop(payload).unwrap();
        if (!res.status) {
          const c = (res as any).conflict as { field?: string } | undefined;
          if (c?.field === "customerCompanyPhoneNumebr") {
            formik.setFieldTouched("customerCompanyPhoneNumebr", true, false);
            formik.setFieldError("customerCompanyPhoneNumebr", res.message || "Phone already used");
            return;
          }
          if (c?.field === "customerCompanyEmail") {
            formik.setFieldTouched("customerCompanyEmail", true, false);
            formik.setFieldError("customerCompanyEmail", res.message || "Email already used");
            return;
          }
          showErrorMessage(res.message);
        } else {
          showMessage("Restaurant updated successfully");
          if (handleCloseDialog) handleCloseDialog(false);
          else navigate(`/shop-history/${id}`);
        }
      } catch (err: any) {
        showErrorMessage(err?.data?.message || err?.message || "Something went wrong");
      }
    },
  });

  // Prefill form when data loads
  useEffect(() => {
    if (!shopData) return;
    const d = shopData;
    // Repair legacy records that saved "2683 Lawrence Ave E" in street name with empty street number.
    const { streetNumber, streetName } = splitStreetNumberFromName(d.customerStreetNumber, d.customerStreetName);
    formik.setValues({
      customerFirstName: d.customerFirstName || "",
      customerLastName: d.customerLastName || "",
      customerMobileNum: d.customerMobileNum ? formatPhone(d.customerMobileNum) : "",
      customerEmail: d.customerEmail || "",
      customerProvince: d.customerProvince || "",
      customerCity: d.customerCity || "",
      customerPostalCode: d.customerPostalCode || "",
      customerStreetNumber: streetNumber,
      customerStreetName: streetName,
      customerDescription: d.customerDescription || "",
      customerCompanyName: d.customerCompanyName || "",
      customerCompanyPhoneNumebr: d.customerCompanyPhoneNumebr ? formatPhone(d.customerCompanyPhoneNumebr) : "",
      customerCompanyEmail: d.customerCompanyEmail || "",
      customerPreferTimeToCall: d.customerPreferTimeToCall || "",
      customerImg: d.customerImg || "",
    });
    // Build address string for display (using the cleaned values)
    const parts = [streetNumber, streetName, d.customerCity, d.customerProvince, d.customerPostalCode].filter(Boolean);
    if (parts.length > 0) setAddressInput(parts.join(", "));
    // Note: we intentionally do NOT force-touch every required field here.
    // Showing red errors under filled fields on open feels broken; the sidebar
    // "Missing: X" panel is the visual cue for required-but-empty fields on
    // load. Field-level errors surface naturally on blur or on Update click.
  }, [shopData]);

  // Live uniqueness checks (debounced) for company phone + email; exclude self
  const phoneUniq = useShopUniquenessCheck("phone", formik.values.customerCompanyPhoneNumebr, id);
  const emailUniq = useShopUniquenessCheck("email", formik.values.customerCompanyEmail, id);

  const formValid = useMemo(() => {
    const v = formik.values;
    const required = [
      v.customerCompanyName, v.customerCompanyPhoneNumebr,
      v.customerFirstName, v.customerLastName, v.customerMobileNum,
      v.customerStreetNumber, v.customerStreetName,
      v.customerCity, v.customerProvince, v.customerPostalCode,
    ];
    if (required.some((f) => !f || !String(f).trim())) return false;
    if (stripPhone(v.customerCompanyPhoneNumebr).length !== 10) return false;
    if (stripPhone(v.customerMobileNum).length !== 10) return false;
    if (Object.keys(formik.errors).length > 0) return false;
    if (phoneUniq.error || phoneUniq.checking) return false;
    if (emailUniq.error || emailUniq.checking) return false;
    return true;
  }, [formik.values, formik.errors, phoneUniq, emailUniq]);

  const handlePhoneChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(field, formatPhone(e.target.value));
  };

  const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d")!;
          let { width, height } = img;
          if (width > height ? width > maxW : height > maxH) {
            if (width > height) { height *= maxW / width; width = maxW; } else { width *= maxH / height; height = maxH; }
          }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => { if (blob) resolve(new File([blob], file.name, { type: file.type })); else reject(new Error("Canvas empty")); }, file.type);
        };
        img.onerror = reject;
      };
    });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return setSelectedImage(undefined);
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)) return showErrorMessage("File must be an image.");
    if (file.size > 2048 * 1024) return showErrorMessage("File must be smaller than 2 MB.");
    try { setSelectedImage(await resizeImage(file, 500, 500)); } catch { showErrorMessage("Failed to process image."); }
  };

  const txProps = { size: "small" as const, fullWidth: true, InputProps: { sx: { fontSize: 14, borderRadius: "8px" } } };

  // ─── Sidebar validation panel ───────────────────────────────────────────
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const validation = useMemo(() => {
    const v = formik.values;
    const mobileDigits = stripPhone(v.customerMobileNum);
    const companyPhoneDigits = stripPhone(v.customerCompanyPhoneNumebr);

    const company = {
      id: "section-company",
      title: "Company Info",
      missing: [
        !v.customerCompanyName.trim() && "Restaurant Name",
        !v.customerCompanyPhoneNumebr.trim()
          ? "Company Phone"
          : companyPhoneDigits.length !== 10 && "Valid Company Phone",
        !v.customerCompanyEmail.trim() && "Company Email",
      ].filter(Boolean) as string[],
      untouched:
        !v.customerCompanyName.trim() &&
        !v.customerCompanyPhoneNumebr.trim() &&
        !v.customerCompanyEmail.trim(),
    };
    const contact = {
      id: "section-contact",
      title: "Contact Person",
      missing: [
        !v.customerFirstName.trim() && "First Name",
        !v.customerLastName.trim() && "Last Name",
      ].filter(Boolean) as string[],
      untouched: !v.customerFirstName.trim() && !v.customerLastName.trim(),
    };
    const phone = {
      id: "section-contact",
      title: "Phone Number",
      missing: [
        !v.customerMobileNum.trim()
          ? "Mobile Number"
          : mobileDigits.length !== 10 && "Valid 10-digit Mobile",
      ].filter(Boolean) as string[],
      untouched: !v.customerMobileNum.trim(),
    };
    const address = {
      id: "section-address",
      title: "Address",
      missing: [
        !v.customerStreetNumber.trim() && "Street No.",
        !v.customerStreetName.trim() && "Street Name",
        !v.customerCity.trim() && "City",
        !v.customerProvince.trim() && "Province",
        !v.customerPostalCode.trim() && "Postal Code",
      ].filter(Boolean) as string[],
      untouched:
        !v.customerStreetNumber.trim() &&
        !v.customerStreetName.trim() &&
        !v.customerCity.trim() &&
        !v.customerProvince.trim() &&
        !v.customerPostalCode.trim(),
    };
    return [company, contact, phone, address].map((s) => ({
      ...s,
      completed: s.missing.length === 0,
    }));
  }, [formik.values]);

  const completedCount = validation.filter((s) => s.completed).length;

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  if (pageLoading) {
    if (openModel !== undefined && handleCloseDialog) {
      return (
        <Dialog open={!!openModel} onClose={() => handleCloseDialog(false)} maxWidth="lg" fullWidth
          PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}>
          <Box sx={{ p: 2 }}><PageLoader height="40vh" /></Box>
        </Dialog>
      );
    }
    return <PageLoader />;
  }

  const content = (
    <Grid container spacing={3}>
      {/* ── Sidebar ── */}
      <Grid item lg={3} md={3} sm={12} xs={12}>
        <Card sx={{ borderRadius: 2, position: "sticky", top: 24 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <IconButton size="small" onClick={() => navigate(`/shop-history/${id}`)} sx={{ width: 28, height: 28 }}>
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography variant="h6" fontWeight={700}>Edit Restaurant</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
              Update the restaurant or shop details. Fields marked with <Box component="span" color="error.main" fontWeight={600}>*</Box> are required.
            </Typography>
            <Divider sx={{ my: 2 }} />

            {/* Progress bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                <Typography fontSize={11} fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                  Progress
                </Typography>
                <Typography fontSize={11} fontWeight={700} color={completedCount === validation.length ? "success.main" : "text.primary"}>
                  {completedCount} / {validation.length}
                </Typography>
              </Box>
              <Box sx={{ height: 6, bgcolor: "grey.100", borderRadius: 3, overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${(completedCount / validation.length) * 100}%`,
                    bgcolor: completedCount === validation.length ? "success.main" : "primary.main",
                    transition: "width 0.3s ease, background-color 0.3s ease",
                    borderRadius: 3,
                  }}
                />
              </Box>
            </Box>

            {/* Section validation list */}
            {validation.map((s) => {
              const isActive = activeSection === s.id;
              const statusColor = s.completed ? "success.main" : !s.untouched ? "warning.main" : "text.disabled";
              return (
                <Box
                  key={s.title}
                  onClick={() => scrollToSection(s.id)}
                  sx={{
                    p: 1.25,
                    mb: 0.75,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    borderLeft: "3px solid",
                    borderColor: isActive ? "primary.main" : "transparent",
                    bgcolor: isActive ? "rgba(0,0,0,0.04)" : "transparent",
                    transition: "all 0.2s ease",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.05)" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                    {s.completed ? (
                      <CheckCircleIcon sx={{ fontSize: 17, color: "success.main" }} />
                    ) : !s.untouched ? (
                      <WarningAmberIcon sx={{ fontSize: 17, color: "warning.main" }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ fontSize: 17, color: "grey.400" }} />
                    )}
                    <Typography fontSize={13} fontWeight={600} color="text.primary">
                      {s.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 3.25,
                      display: "block",
                      lineHeight: 1.4,
                      color: statusColor,
                      fontWeight: 500,
                    }}
                  >
                    {s.completed
                      ? "Completed"
                      : s.missing.length > 0
                      ? `Missing: ${s.missing.join(", ")}`
                      : "Required"}
                  </Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Grid>

      {/* ── Form ── */}
      <Grid item lg={9} md={9} sm={12} xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>

            {/* Profile image */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <SectionHeader icon={<CameraAltIcon fontSize="small" />} title="Profile Photo" />
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={3}>
                    <Box sx={{ position: "relative", flexShrink: 0 }}>
                      <Avatar
                        src={selectedImage ? URL.createObjectURL(selectedImage) : existingImage ? `${LARAVEL_URL}/${existingImage}` : undefined}
                        sx={{ width: 88, height: 88, bgcolor: "primary.light", fontSize: 32 }}
                      >
                        {formik.values.customerCompanyName?.[0]?.toUpperCase() || formik.values.customerFirstName?.[0]?.toUpperCase() || "S"}
                      </Avatar>
                      {(selectedImage || existingImage) && (
                        <Tooltip title="Remove photo">
                          <IconButton size="small" onClick={() => { setSelectedImage(undefined); setExistingImage(""); }}
                            sx={{ position: "absolute", top: -6, right: -6, bgcolor: "error.main", color: "#fff", width: 22, height: 22, "&:hover": { bgcolor: "error.dark" } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Box>
                      <input ref={fileInputRef} accept="image/*" style={{ display: "none" }} id="shop-img-upload" type="file" onChange={handleImageChange} />
                      <label htmlFor="shop-img-upload">
                        <Button variant="outlined" component="span" startIcon={<CameraAltIcon />} size="small" sx={{ borderRadius: "8px", textTransform: "none" }}>
                          {selectedImage || existingImage ? "Change Photo" : "Upload Photo"}
                        </Button>
                      </label>
                      <Typography variant="caption" display="block" color="text.disabled" mt={0.5}>JPG, PNG or GIF — max 2 MB</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Company info */}
            <Grid item xs={12}>
              <Card id="section-company" onFocus={() => setActiveSection("section-company")} sx={{ borderRadius: 2, scrollMarginTop: 24 }}>
                <SectionHeader icon={<BusinessIcon fontSize="small" />} title="Restaurant Details" />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2.5}>
                    <Field label="Restaurant Name" cols={12} required>
                      <TextField placeholder="e.g. The Golden Fork Restaurant" {...txProps} {...formik.getFieldProps("customerCompanyName")}
                        error={formik.touched.customerCompanyName && !!formik.errors.customerCompanyName}
                        helperText={(formik.touched.customerCompanyName && formik.errors.customerCompanyName) || ""}
                        InputProps={{ ...txProps.InputProps, startAdornment: <InputAdornment position="start"><BusinessIcon fontSize="small" color="action" /></InputAdornment> }} />
                    </Field>
                    <Field label="Company Phone" required>
                      <TextField placeholder="(416) 555-1234" {...txProps} value={formik.values.customerCompanyPhoneNumebr}
                        onChange={handlePhoneChange("customerCompanyPhoneNumebr")} onBlur={() => formik.setFieldTouched("customerCompanyPhoneNumebr")}
                        error={(formik.touched.customerCompanyPhoneNumebr && !!formik.errors.customerCompanyPhoneNumebr) || !!phoneUniq.error}
                        helperText={(formik.touched.customerCompanyPhoneNumebr && formik.errors.customerCompanyPhoneNumebr) || phoneUniq.error || (phoneUniq.checking ? "Checking…" : "")}
                        InputProps={{ ...txProps.InputProps,
                          startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment>,
                          endAdornment: formik.values.customerCompanyPhoneNumebr && stripPhone(formik.values.customerCompanyPhoneNumebr).length === 10 && !phoneUniq.error && !phoneUniq.checking ? <InputAdornment position="end"><Chip label="Valid" size="small" color="success" variant="outlined" /></InputAdornment> : null,
                        }} />
                    </Field>
                    <Field label="Company Email">
                      <TextField placeholder="info@restaurant.com" type="email" {...txProps} {...formik.getFieldProps("customerCompanyEmail")}
                        error={(formik.touched.customerCompanyEmail && !!formik.errors.customerCompanyEmail) || !!emailUniq.error}
                        helperText={(formik.touched.customerCompanyEmail && formik.errors.customerCompanyEmail) || emailUniq.error || (emailUniq.checking ? "Checking…" : "")}
                        InputProps={{ ...txProps.InputProps, startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment> }} />
                    </Field>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Card id="section-address" onFocus={() => setActiveSection("section-address")} sx={{ borderRadius: 2, scrollMarginTop: 24 }}>
                <SectionHeader icon={<LocationOnIcon fontSize="small" />} title="Address" />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Typography variant="body2" fontWeight={500} mb={0.5} color="text.secondary">Search Address</Typography>
                      <Box sx={{ position: "relative" }}>
                        <TextField fullWidth size="small" placeholder="Start typing an address to auto-fill fields below..."
                          value={addressInput} onChange={(e) => handleAddressInputChange(e.target.value)}
                          onBlur={() => setTimeout(() => setAddressOpen(false), 200)}
                          onFocus={() => addressOptions.length > 0 && setAddressOpen(true)}
                          InputProps={{ sx: { fontSize: 14, borderRadius: "8px" },
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
                            endAdornment: addressLoading ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
                          }} />
                        {addressOpen && addressOptions.length > 0 && (
                          <Paper elevation={4} sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1300, maxHeight: 260, overflowY: "auto", mt: 0.5, borderRadius: "8px" }}>
                            {addressOptions.map((opt, idx) => (
                              <Box key={idx} onMouseDown={() => handleAddressSelect(opt)}
                                sx={{ display: "flex", alignItems: "flex-start", gap: 1, px: 2, py: 1.2, cursor: "pointer", fontSize: 13,
                                  borderBottom: idx < addressOptions.length - 1 ? "1px solid" : "none", borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}>
                                <LocationOnIcon fontSize="small" sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }} />
                                <Typography variant="body2" lineHeight={1.4}>{opt.display_name}</Typography>
                              </Box>
                            ))}
                          </Paper>
                        )}
                      </Box>
                      <Typography variant="caption" color="text.disabled">Selecting a result will auto-fill the fields below</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ whiteSpace: "nowrap", letterSpacing: 1, fontWeight: 600 }}
                        >
                          OR FILL MANUALLY
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    </Grid>
                    <Field label="Street No." cols={3} required>
                      <TextField placeholder="123" {...txProps} {...formik.getFieldProps("customerStreetNumber")}
                        error={formik.touched.customerStreetNumber && !!formik.errors.customerStreetNumber}
                        helperText={formik.touched.customerStreetNumber && formik.errors.customerStreetNumber} />
                    </Field>
                    <Field label="Street Name" cols={9} required>
                      <TextField placeholder="Main Street" {...txProps} {...formik.getFieldProps("customerStreetName")}
                        error={formik.touched.customerStreetName && !!formik.errors.customerStreetName}
                        helperText={formik.touched.customerStreetName && formik.errors.customerStreetName} />
                    </Field>
                    <Field label="City" cols={6} required>
                      <TextField placeholder="Toronto" {...txProps} {...formik.getFieldProps("customerCity")}
                        error={formik.touched.customerCity && !!formik.errors.customerCity}
                        helperText={formik.touched.customerCity && formik.errors.customerCity} />
                    </Field>
                    <Field label="Province" cols={6} required>
                      <TextField placeholder="Ontario" {...txProps} {...formik.getFieldProps("customerProvince")}
                        error={formik.touched.customerProvince && !!formik.errors.customerProvince}
                        helperText={formik.touched.customerProvince && formik.errors.customerProvince} />
                    </Field>
                    <Field label="Postal Code" cols={6} required>
                      <TextField placeholder="M5V 2T6" {...txProps} {...formik.getFieldProps("customerPostalCode")}
                        onChange={(e) => formik.setFieldValue("customerPostalCode", e.target.value.toUpperCase())}
                        error={formik.touched.customerPostalCode && !!formik.errors.customerPostalCode}
                        helperText={formik.touched.customerPostalCode && formik.errors.customerPostalCode} />
                    </Field>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Contact person */}
            <Grid item xs={12}>
              <Card id="section-contact" onFocus={() => setActiveSection("section-contact")} sx={{ borderRadius: 2, scrollMarginTop: 24 }}>
                <SectionHeader icon={<PersonIcon fontSize="small" />} title="Contact Person" />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2.5}>
                    <Field label="First Name" required>
                      <TextField placeholder="John" {...txProps} {...formik.getFieldProps("customerFirstName")}
                        error={formik.touched.customerFirstName && !!formik.errors.customerFirstName}
                        helperText={formik.touched.customerFirstName && formik.errors.customerFirstName} />
                    </Field>
                    <Field label="Last Name" required>
                      <TextField placeholder="Smith" {...txProps} {...formik.getFieldProps("customerLastName")}
                        error={formik.touched.customerLastName && !!formik.errors.customerLastName}
                        helperText={formik.touched.customerLastName && formik.errors.customerLastName} />
                    </Field>
                    <Field label="Mobile Number" required>
                      <TextField placeholder="(416) 555-1234" {...txProps} value={formik.values.customerMobileNum}
                        onChange={handlePhoneChange("customerMobileNum")} onBlur={() => formik.setFieldTouched("customerMobileNum")}
                        error={formik.touched.customerMobileNum && !!formik.errors.customerMobileNum}
                        helperText={formik.touched.customerMobileNum && formik.errors.customerMobileNum}
                        InputProps={{ ...txProps.InputProps,
                          startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" color="action" /></InputAdornment>,
                          endAdornment: formik.values.customerMobileNum && stripPhone(formik.values.customerMobileNum).length === 10 ? <InputAdornment position="end"><Chip label="Valid" size="small" color="success" variant="outlined" /></InputAdornment> : null,
                        }} />
                    </Field>
                    <Field label="Email">
                      <TextField placeholder="john@restaurant.com" type="email" {...txProps} {...formik.getFieldProps("customerEmail")}
                        error={formik.touched.customerEmail && !!formik.errors.customerEmail} helperText={formik.touched.customerEmail && formik.errors.customerEmail}
                        InputProps={{ ...txProps.InputProps, startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" color="action" /></InputAdornment> }} />
                    </Field>
                    <Field label="Preferred Time to Call" cols={12}>
                      <Select fullWidth size="small" displayEmpty value={formik.values.customerPreferTimeToCall}
                        onChange={(e) => formik.setFieldValue("customerPreferTimeToCall", e.target.value)}
                        startAdornment={<InputAdornment position="start"><AccessTimeIcon fontSize="small" color="action" /></InputAdornment>}
                        sx={{ fontSize: 14, borderRadius: "8px" }}
                        renderValue={(val) => val ? <Typography fontSize={14}>{val as string}</Typography> : <Typography fontSize={14} color="text.disabled">Select preferred call time</Typography>}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300, borderRadius: "8px" } } }}>
                        <MenuItem value="" sx={{ color: "text.disabled", fontSize: 13 }}>— Select time —</MenuItem>
                        <MenuItem disabled sx={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>TIME RANGES</MenuItem>
                        {TIME_SLOTS.slice(0, 6).map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                        <MenuItem disabled sx={{ fontSize: 11, opacity: 0.6, fontWeight: 600, letterSpacing: 0.5 }}>SPECIFIC TIMES</MenuItem>
                        {TIME_SLOTS.slice(6).map((t) => <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>)}
                      </Select>
                    </Field>
                    <Field label="Notes / Description" cols={12}>
                      <TextField placeholder="Any special instructions, delivery notes, or comments..." {...txProps} multiline rows={4}
                        {...formik.getFieldProps("customerDescription")}
                        InputProps={{ sx: { fontSize: 14, borderRadius: "8px", alignItems: "flex-start" }, startAdornment: <InputAdornment position="start" sx={{ mt: 1.2 }}><NotesIcon fontSize="small" color="action" /></InputAdornment> }} />
                    </Field>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="flex-end" gap={1.5}>
                    <Button variant="outlined" startIcon={<CloseIcon />} onClick={() => handleCloseDialog ? handleCloseDialog(false) : navigate(`/shop-history/${id}`)}
                      sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}>Cancel</Button>
                    <Button variant="contained" onClick={() => formik.handleSubmit()} disabled={!formValid || isLoading || isImageUploading}
                      startIcon={isLoading || isImageUploading ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />}
                      sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}>
                      {isLoading || isImageUploading ? "Saving..." : "Update Restaurant"}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );

  if (openModel !== undefined && handleCloseDialog) {
    return (
      <Dialog open={!!openModel} onClose={() => handleCloseDialog(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}>
        <Box sx={{ p: 2, overflowY: "auto" }}>{content}</Box>
      </Dialog>
    );
  }

  return content;
};

export default ShopEdit;
